/**
 * `resource-cache.ts` — phase 67. The dashboard SPA's single shared,
 * module-level client cache for READ data.
 *
 * # Why this exists
 *
 * Before phase 67 every page fetched in a `useEffect` into local
 * `useState`, guarded with a `cancelled` flag, and discarded the result
 * on unmount. Navigating Live → Data → Live re-ran every fetch and
 * flashed a spinner each time, even though the data was on screen
 * seconds ago. There was no shared client cache that survived a route
 * change.
 *
 * # What this is
 *
 * A tiny in-house store (a module-level `Map`, NOT React state/context
 * that unmounts) plus a `useCachedResource(key, fetcher, opts?)` hook
 * that is the SOLE fetch path for primary read data. Semantics:
 *
 *   - **Module scope.** The store lives at module scope so cached
 *     entries outlive component unmounts + route changes within one SPA
 *     session. (Reload-persistence to sessionStorage is explicitly OUT
 *     of scope — sub-decision B — to avoid PII-at-rest questions.)
 *   - **Stale-while-revalidate** (sub-decision C). On mount the hook
 *     returns the cached value SYNCHRONOUSLY if present; it triggers a
 *     background refetch only when the entry is older than the
 *     per-resource `staleTime` (default `DEFAULT_STALE_TIME_MS`) or has
 *     been explicitly invalidated. The refetched value replaces the
 *     entry and re-renders subscribers. A cold cache keeps today's
 *     loading state (sub-decision F).
 *   - **Mutation invalidation** (sub-decision D). Every SPA write calls
 *     `invalidate(keyOrPrefix)` for the resources it touches; keys are
 *     namespaced strings so a single `invalidate("data:")` clears every
 *     entity view. An invalidated entry refetches on next read.
 *   - **Session isolation** (sub-decision E). `clearCache()` drops the
 *     WHOLE store; it is wired at the sign-out / stale-session boundary
 *     so no studio's data can ever render under another session.
 *
 * # No new dependency (sub-decision A)
 *
 * Implemented by hand on `useSyncExternalStore` (React 18) — no
 * `@tanstack/react-query` / SWR / any caching library. Rejected
 * alternative: react-query would trip the "No new dependencies"
 * invariant and the existing hand-rolled `localStorage` caching already
 * sets the house style.
 *
 * The store logic (`peekEntry` / `setEntry` / `invalidate` /
 * `clearCache` / `isStale`) is exported as pure, DOM-free functions so
 * it is unit-testable under the root `*.test.ts` runner (no jsdom in
 * this repo); the React hook is the thin wrapper on top.
 */

import { useCallback, useDebugValue, useEffect, useSyncExternalStore } from "react";

/** Default per-entry staleness window (sub-decision C — a small TTL). */
export const DEFAULT_STALE_TIME_MS = 30_000;

/**
 * One cached resource. `value` is the last successful fetcher result;
 * `fetchedAt` is the epoch-ms it landed; `error` is the last failure
 * (kept alongside a stale value so the page can decide what to show);
 * `inflight` is the in-progress refetch promise (deduped so two mounts
 * of the same key don't double-fetch).
 */
interface CacheEntry<T> {
  value: T | undefined;
  hasValue: boolean;
  fetchedAt: number;
  error: Error | null;
  inflight: Promise<void> | null;
}

type AnyEntry = CacheEntry<unknown>;

/**
 * Module-level store. Survives route changes; dropped only by
 * `clearCache()`. NOT React state — that would unmount with the tree.
 */
const store = new Map<string, AnyEntry>();

/** Per-key subscriber sets. A bump notifies every mounted hook on that key. */
const subscribers = new Map<string, Set<() => void>>();

/**
 * Extra teardown callbacks run by `clearCache()` (sub-decision E). A page
 * that keeps its OWN module-level cache outside this store (e.g. the Data
 * browser's `dataPageCache` — a typed `Map` with realtime-push semantics
 * that don't fit the generic hook) registers a clearer here so a single
 * sign-out / stale-session `clearCache()` wipes EVERY cache, not just this
 * store. Without this, a sibling module cache would leak one studio's rows
 * into the next session.
 */
const clearListeners = new Set<() => void>();

/**
 * Register a callback invoked whenever the whole cache is cleared
 * (sign-out / stale-session). Returns an unsubscribe fn. Module-level
 * caches call this once at module load and never unregister.
 */
export function registerClearListener(fn: () => void): () => void {
  clearListeners.add(fn);
  return () => {
    clearListeners.delete(fn);
  };
}

/**
 * Monotonic per-key version counter. `useSyncExternalStore`'s
 * `getSnapshot` must return a referentially-stable value while nothing
 * changed; we return a `{version, entry}` snapshot object that is only
 * re-created when the version bumps, so React doesn't loop.
 */
const versions = new Map<string, number>();
const snapshots = new Map<string, ResourceSnapshot<unknown>>();

/** Clock seam — overridable in tests so staleness is deterministic. */
let clock: () => number = () => Date.now();

/** TEST-ONLY: install a deterministic clock. Returns a restore fn. */
export function __setClockForTest(fn: () => number): () => void {
  const prev = clock;
  clock = fn;
  return () => {
    clock = prev;
  };
}

function bump(key: string): void {
  versions.set(key, (versions.get(key) ?? 0) + 1);
  // Invalidate the memoised snapshot so the next getSnapshot rebuilds it.
  snapshots.delete(key);
  const subs = subscribers.get(key);
  if (subs) for (const s of subs) s();
}

/**
 * Read the current entry for a key WITHOUT mounting a subscriber or
 * triggering a fetch. Pure — used by tests + by the hook's snapshot.
 */
export function peekEntry<T>(key: string): CacheEntry<T> | undefined {
  return store.get(key) as CacheEntry<T> | undefined;
}

/**
 * Whether `key`'s entry is stale (absent, never attempted, explicitly
 * invalidated via `fetchedAt = -Infinity`, or older than `staleTime`).
 *
 * An entry that ERRORED — with or without a retained value — is NOT stale
 * until `staleTime` has passed since the failed attempt: the failure is
 * the backoff. Before staff-session-slice-1 (2026-09-27) a valueless
 * errored entry short-circuited to `true` here, before the clock was
 * consulted, so a dead session re-fetched `/api/catalog` on every read
 * tick (~20 requests a second) instead of once per window. `invalidate()`
 * still forces one immediate retry, because it resets `fetchedAt`.
 */
export function isStale(key: string, staleTime: number): boolean {
  const e = store.get(key);
  if (e === undefined) return true;
  if (!e.hasValue && e.error === null) return true;
  return clock() - e.fetchedAt >= staleTime;
}

/**
 * Overwrite the cached value for a key + notify subscribers. Pure setter
 * (no fetch). Mainly internal, but exported for tests.
 */
export function setEntry<T>(key: string, value: T): void {
  const existing = store.get(key);
  store.set(key, {
    value,
    hasValue: true,
    fetchedAt: clock(),
    error: null,
    inflight: existing?.inflight ?? null,
  });
  bump(key);
}

/**
 * Invalidate by EXACT key or by PREFIX (sub-decision D). Matching
 * entries are marked stale (value retained so stale-while-revalidate can
 * still render it) so the next read refetches. Subscribers are notified
 * so any mounted hook on a matched key kicks off its background refetch.
 *
 * Prefix matching is plain `String.startsWith`, so `invalidate("data:")`
 * clears every `data:<entity>:…` entry, and an exact key like
 * `"data-counts"` clears just that one (it's a prefix of itself).
 */
export function invalidate(keyOrPrefix: string): void {
  for (const [key, entry] of store) {
    if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
      // Force-stale: a sentinel `fetchedAt` guarantees `isStale` is true
      // for ANY positive staleTime without discarding the value.
      entry.fetchedAt = Number.NEGATIVE_INFINITY;
      bump(key);
    }
  }
}

/**
 * Drop the ENTIRE store (sub-decision E). Wired at sign-out /
 * stale-session so no studio's data can render under another session.
 * Notifies every subscriber so mounted hooks fall back to their cold
 * loading state + refetch.
 */
export function clearCache(): void {
  const keys = Array.from(store.keys());
  store.clear();
  for (const key of keys) bump(key);
  // Wipe sibling module caches (e.g. the Data browser's page cache) so
  // session isolation covers EVERY cache, not just this store.
  for (const fn of clearListeners) fn();
}

/** The shape the hook returns to a component. */
export interface ResourceSnapshot<T> {
  /** Last successful value, or `undefined` on a cold/cleared cache. */
  readonly data: T | undefined;
  /** True while a fetch is in flight AND there is no cached value yet. */
  readonly loading: boolean;
  /** True while a background revalidation runs over an existing value. */
  readonly revalidating: boolean;
  /** Last fetch error (retained alongside a stale value), else null. */
  readonly error: Error | null;
}

export interface UseCachedResourceOptions {
  /** Per-resource staleness window in ms. Default `DEFAULT_STALE_TIME_MS`. */
  readonly staleTime?: number;
  /**
   * When false, the hook neither fetches nor subscribes (returns a cold
   * snapshot). Used to gate a fetch on a prerequisite (e.g. a slug).
   */
  readonly enabled?: boolean;
}

/**
 * Run (or join) a fetch for `key`. Dedupes concurrent fetches via the
 * entry's `inflight` promise so two mounts of the same key issue one
 * network call. On success stores the value; on failure stores the error
 * (keeping any prior value so a stale render survives a transient
 * failure) and stamps the attempt time, so the failure backs off for one
 * `staleTime` rather than retrying on the next read tick. Always notifies
 * subscribers.
 */
function runFetch<T>(key: string, fetcher: () => Promise<T>): Promise<void> {
  const existing = store.get(key) as CacheEntry<T> | undefined;
  if (existing?.inflight) return existing.inflight;

  const base: CacheEntry<T> = existing ?? {
    value: undefined,
    hasValue: false,
    fetchedAt: 0,
    error: null,
    inflight: null,
  };

  const p = fetcher()
    .then((value) => {
      store.set(key, {
        value,
        hasValue: true,
        fetchedAt: clock(),
        error: null,
        inflight: null,
      });
    })
    .catch((err: unknown) => {
      const cur = store.get(key) as CacheEntry<T> | undefined;
      store.set(key, {
        value: cur?.value,
        hasValue: cur?.hasValue ?? false,
        // The failed attempt starts its own staleness window: a retained
        // value keeps rendering (stale-while-revalidate) and the next retry
        // waits `staleTime`, whether the entry was cold, valued, or
        // force-stale. Leaving `fetchedAt` untouched here is what made an
        // errored entry retry on every read tick (the 2026-09-27 401 storm).
        fetchedAt: clock(),
        error: err instanceof Error ? err : new Error(String(err)),
        inflight: null,
      });
    })
    .finally(() => {
      bump(key);
    });

  store.set(key, { ...base, inflight: p });
  return p;
}

/**
 * TEST-ONLY synchronous accessor: returns a resource's snapshot WITHOUT
 * mounting React, and (optionally) kicks a fetch if stale — mirrors what
 * the hook does on mount so store behaviour can be asserted in a
 * DOM-free vitest file.
 */
export function __readForTest<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: UseCachedResourceOptions,
): { snapshot: ResourceSnapshot<T>; fetched: boolean } {
  const staleTime = opts?.staleTime ?? DEFAULT_STALE_TIME_MS;
  let fetched = false;
  if (isStale(key, staleTime)) {
    fetched = store.get(key)?.inflight == null;
    void runFetch(key, fetcher);
  }
  return { snapshot: readSnapshot<T>(key), fetched };
}

function readSnapshot<T>(key: string): ResourceSnapshot<T> {
  const e = store.get(key) as CacheEntry<T> | undefined;
  if (e === undefined) {
    return { data: undefined, loading: true, revalidating: false, error: null };
  }
  return {
    data: e.value,
    loading: !e.hasValue && e.inflight !== null,
    revalidating: e.hasValue && e.inflight !== null,
    error: e.error,
  };
}

/**
 * Memoised, referentially-stable snapshot keyed off the per-key version
 * counter — required so `useSyncExternalStore`'s `getSnapshot` returns
 * the same object reference while nothing changed (otherwise React loops
 * with a "getSnapshot should be cached" warning).
 */
function getStableSnapshot<T>(key: string): ResourceSnapshot<T> {
  const version = versions.get(key) ?? 0;
  const cached = snapshots.get(key) as
    | (ResourceSnapshot<T> & { __version?: number })
    | undefined;
  if (cached && (cached as { __version?: number }).__version === version) {
    return cached;
  }
  const fresh = readSnapshot<T>(key) as ResourceSnapshot<T> & {
    __version?: number;
  };
  (fresh as { __version?: number }).__version = version;
  snapshots.set(key, fresh as ResourceSnapshot<unknown>);
  return fresh;
}

/**
 * What a hook with `enabled: false` reads: ONE frozen object for every key.
 * The stable-reference rule above holds here too — a fresh
 * `{data: undefined, …}` per call reads as a change on every render, and
 * React loops until "Maximum update depth exceeded" (newsletter-tag-audience,
 * 2026-10-02: `/newsletter` was the first page to mount the hook disabled).
 */
const DISABLED_SNAPSHOT: ResourceSnapshot<never> = Object.freeze({
  data: undefined,
  loading: false,
  revalidating: false,
  error: null,
});

/**
 * The hook's `getSnapshot`: the same object on every call while nothing
 * changed, enabled or not. Exported so the DOM-free test pins it.
 */
export function getHookSnapshot<T>(key: string, enabled: boolean): ResourceSnapshot<T> {
  return enabled ? getStableSnapshot<T>(key) : DISABLED_SNAPSHOT;
}

function subscribe(key: string, cb: () => void): () => void {
  let set = subscribers.get(key);
  if (set === undefined) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(cb);
  return () => {
    const s = subscribers.get(key);
    if (s) {
      s.delete(cb);
      if (s.size === 0) subscribers.delete(key);
    }
  };
}

/**
 * The SOLE read-fetch hook for the dashboard. Returns the cached value
 * synchronously if present; revalidates in the background when stale or
 * invalidated; re-renders on store changes (including `invalidate` and
 * `clearCache`).
 */
export function useCachedResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: UseCachedResourceOptions,
): ResourceSnapshot<T> {
  const staleTime = opts?.staleTime ?? DEFAULT_STALE_TIME_MS;
  const enabled = opts?.enabled ?? true;

  const subscribeFn = useCallback(
    (cb: () => void) => (enabled ? subscribe(key, cb) : () => {}),
    [key, enabled],
  );
  const getSnapshot = useCallback(
    (): ResourceSnapshot<T> => getHookSnapshot<T>(key, enabled),
    [key, enabled],
  );

  const snapshot = useSyncExternalStore(subscribeFn, getSnapshot, getSnapshot);
  useDebugValue(snapshot);

  // Effect-driven fetch: on mount, on key change, and whenever the store
  // bumps to a stale state for this key (e.g. after `invalidate`). The
  // `snapshot` dep re-runs this after each store change so an
  // invalidation triggers the background refetch.
  useEffect(() => {
    if (!enabled) return;
    if (isStale(key, staleTime)) {
      void runFetch(key, fetcher);
    }
    // `fetcher` is intentionally NOT a dep — callers pass a fresh closure
    // every render; the key is the cache identity. `snapshot` is the dep
    // that re-arms the fetch after an invalidate/clear bump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, staleTime, snapshot]);

  return snapshot;
}
